/**
 * Universal Product Card Actions Handler
 * Handles add to cart, quantity controls (+/-), and cart synchronization
 * Works with card-product-hits component
 */

class ProductCardActions {
  constructor(container = document) {
    this.container = container;
    this.cartState = {};
    this.headerCartTotalValueEl = document.querySelector('.cart-total-price .totals__total-value');
    this.headerCartTotalValueClassName = this.headerCartTotalValueEl?.className || null;
    this.headerCartTotalValueExampleText = this.headerCartTotalValueEl?.textContent?.trim() || null;
    
    // Cart data cache to reduce fetch requests
    this.cartDataCache = null;
    this.cartDataCacheTime = 0;
    this.CART_CACHE_DURATION = 1500; // 1.5 seconds cache
    
    this.init();
  }

  init() {
    this.initializeCart();
    this.setupEventListeners();
    this.setupPubSubListeners();
    this.setupPageVisibilityHandlers();
    this.syncCardsWithCart();
  }
  
  setupPageVisibilityHandlers() {
    // Синхронизация при возврате на страницу (кнопка "назад" браузера)
    window.addEventListener('pageshow', (event) => {
      // event.persisted = true означает, что страница загружена из bfcache (back-forward cache)
      if (event.persisted) {
        console.log('[ProductCardActions] Page restored from cache, syncing...');
        this.cartDataCache = null; // Сбрасываем кеш
        this.syncCardsWithCart();
      }
    });
    
    // Синхронизация когда вкладка становится видимой
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        console.log('[ProductCardActions] Tab became visible, syncing...');
        this.cartDataCache = null; // Сбрасываем кеш
        this.syncCardsWithCart();
      }
    });
  }

  /**
   * Вычисляет total price минус стоимость упаковки
   */
  getTotalWithoutPackaging(cart) {
    if (!cart || !cart.items) return 0;

    let packagingCost = 0;
    
    // Получаем ID упаковок из конфига
    if (window.PackagingUtils) {
      const config = window.PackagingUtils.getConfig();
      if (config && config.frozenId && config.cooledId) {
        const frozenId = String(config.frozenId);
        const cooledId = String(config.cooledId);
        
        cart.items.forEach(item => {
          const variantId = String(item.variant_id || item.id);
          if (variantId === frozenId || variantId === cooledId) {
            packagingCost += item.final_line_price || 0;
          }
        });
      }
    }

    return cart.total_price - packagingCost;
  }

  formatCartTotalPrice(totalPriceCents) {
    const locale = document.documentElement.lang || 'de';
    const numeric = (totalPriceCents / 100).toLocaleString(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    const example = this.headerCartTotalValueExampleText || '';
    const symbolMatch = example.match(/[^\d\s.,]+/);
    const symbol = symbolMatch ? symbolMatch[0] : '€';
    const isPrefix = example.trim().startsWith(symbol);

    return isPrefix ? `${symbol}${numeric}` : `${numeric} ${symbol}`.trim();
  }

  updateCartSectionsWithoutOpen(parsedState) {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer || !parsedState || !parsedState.sections) return;

    cartDrawer.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      const html = parsedState.sections[section.id];
      if (!html) return;

      sectionElement.innerHTML = cartDrawer.getSectionInnerHTML(html, section.selector);
    });

    const overlay = cartDrawer.querySelector('#CartDrawer-Overlay');
    if (overlay) overlay.addEventListener('click', cartDrawer.close.bind(cartDrawer));
  }

  async updateCartHeader(cartData = null) {
    try {
      // Use provided cartData or fetch from cache/API
      const cart = cartData || await this.fetchCartData();
      this.updateCartHeaderUI(cart);
    } catch (error) {
      // Silent error handling
    }
  }

  updateCartHeaderUI(cart) {
    document.querySelectorAll('.cart-count-bubble span, [data-cart-count]').forEach(el => {
      el.textContent = cart.item_count;
    });
    
    // Вычисляем total без упаковки
    const totalWithoutPackaging = this.getTotalWithoutPackaging(cart);
    
    // Обновляем ВСЕ элементы цены (десктоп + мобильная корзина)
    document.querySelectorAll('.cart-total-price').forEach(priceContainer => {
      let valueEl = priceContainer.querySelector('.totals__total-value');
      if (!valueEl) {
        valueEl = document.createElement('span');
        valueEl.className = this.headerCartTotalValueClassName || 'totals__total-value t3';
        priceContainer.innerHTML = '';
        priceContainer.appendChild(valueEl);
      }
      valueEl.textContent = this.formatCartTotalPrice(totalWithoutPackaging);
    });
    
    // Обновляем все элементы с data-bottom-cart-total (мобильная корзина)
    document.querySelectorAll('[data-bottom-cart-total]').forEach(el => {
      el.textContent = this.formatCartTotalPrice(totalWithoutPackaging);
    });
    
    // Обновляем иконки корзины (пустая/полная) в мобильной корзине
    const isEmpty = !cart || cart.item_count === 0;
    document.querySelectorAll('[data-cart-icon-empty]').forEach(icon => {
      if (isEmpty) {
        icon.removeAttribute('hidden');
      } else {
        icon.setAttribute('hidden', '');
      }
    });
    document.querySelectorAll('[data-cart-icon-full]').forEach(icon => {
      if (isEmpty) {
        icon.setAttribute('hidden', '');
      } else {
        icon.removeAttribute('hidden');
      }
    });
    
    // Обновляем бэйдж в мобильной корзине
    document.querySelectorAll('[data-bottom-cart-badge]').forEach(badge => {
      if (isEmpty) {
        badge.setAttribute('hidden', '');
      } else {
        badge.removeAttribute('hidden');
        badge.textContent = cart.item_count;
      }
    });
    
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
      bubbles: true
    }));
  }

  async fetchCartData(useCache = true) {
    if (useCache) {
      const now = Date.now();
      if (this.cartDataCache && (now - this.cartDataCacheTime) < this.CART_CACHE_DURATION) {
        return this.cartDataCache;
      }
    }
    
    const response = await fetch('/cart.js');
    const cart = await response.json();
    
    // Update cache
    this.cartDataCache = cart;
    this.cartDataCacheTime = Date.now();
    
    return cart;
  }

  async initializeCart() {
    try {
      const cart = await this.fetchCartData(false); // Initial load, no cache
      
      cart.items.forEach(item => {
        const variantId = item.variant_id.toString();
        const productId = item.product_id.toString();
        
        const form = this.container.querySelector(`.hits-add-form input[value="${variantId}"]`)?.closest('form');
        if (form) {
          const productIdAttr = form.querySelector('.hits-add-btn-icon')?.dataset.productId;
          if (productIdAttr) {
            this.cartState[productIdAttr] = item.quantity;
          }
        }
      });
      
      this.container.querySelectorAll('.hits-add-form').forEach(form => {
        const addButton = form.querySelector('.hits-add-btn-icon');
        const productId = addButton?.dataset.productId;
        if (productId && this.cartState[productId] > 0) {
          const qtyControls = form.querySelector('.hits-quantity-controls');
          const qtyDisplay = form.querySelector('.hits-qty-display');
          const priceDisplay = form.querySelector('.hits-add-wrapper__price');
          
          if (addButton) addButton.style.display = 'none';
          if (priceDisplay) priceDisplay.style.display = 'none';
          if (qtyControls) qtyControls.style.display = 'flex';
          if (qtyDisplay) qtyDisplay.textContent = this.cartState[productId];
        }
      });
    } catch (error) {
      // Silent error - cart will sync on first interaction
    }
  }

  setupPubSubListeners() {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        this.syncCardsWithCart();
      });
      
      subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        this.handleVariantChange(event);
      });
    } else {
      document.addEventListener('cart:refresh', () => {
        this.syncCardsWithCart();
      });
    }
  }

  handleVariantChange(event) {
    const sectionId = event.data?.sectionId;
    const variant = event.data?.variant;
    if (!sectionId || !variant || !variant.id || variant.price === undefined || variant.price === null) return;

    const esc = window.CSS && typeof window.CSS.escape === 'function' ? window.CSS.escape : (s) => String(s).replace(/"/g, '\\"');
    const productInfoEl = document.querySelector(`product-info[data-section="${esc(sectionId)}"]`);

    const quickAddModal = productInfoEl?.closest('quick-add-modal');
    const modalId = quickAddModal?.id || '';
    const productId = modalId.startsWith('QuickAdd-') ? modalId.replace('QuickAdd-', '') : null;
    if (!productId) return;

    const wrapper = this.container.querySelector(`.hits-add-wrapper[data-product-id="${productId}"]`);
    if (!wrapper) return;

    const priceDisplay = wrapper.querySelector('[data-price-display]');
    if (priceDisplay) {
      const pricePrefix = priceDisplay.querySelector('.price-prefix');
      if (pricePrefix) pricePrefix.remove();

      const currentEl = priceDisplay.querySelector('[data-price-current]');
      if (currentEl) {
        currentEl.textContent = this.formatCartTotalPrice(variant.price);
      }

      const compareAt = variant.compare_at_price;
      const hasDiscount = compareAt && Number(compareAt) > Number(variant.price);
      let oldEl = priceDisplay.querySelector('[data-price-old]');
      if (hasDiscount) {
        if (!oldEl) {
          oldEl = document.createElement('span');
          oldEl.className = 'hits-price-old t3';
          oldEl.setAttribute('data-price-old', '');
          priceDisplay.appendChild(oldEl);
        }
        oldEl.textContent = this.formatCartTotalPrice(compareAt);
      } else if (oldEl) {
        oldEl.remove();
      }
    }

    const form = wrapper.closest('form');
    if (form) {
      const variantInput = form.querySelector('input[name="id"]');
      if (variantInput) variantInput.value = variant.id;
      form.dataset.activeVariantId = variant.id.toString();
    }
  }

  async syncCardsWithCart() {
    try {
      // Принудительно запрашиваем свежие данные корзины (без кеша)
      const cart = await this.fetchCartData(false);
      
      // Полностью сбрасываем cartState
      this.cartState = {};
      
      const variantsByProduct = {};
      cart.items.forEach(item => {
        const productId = item.product_id.toString();
        const variantId = item.variant_id.toString();
        if (!variantsByProduct[productId]) variantsByProduct[productId] = [];
        variantsByProduct[productId].push({ variantId, quantity: item.quantity });
      });

      Object.keys(variantsByProduct).forEach((productId) => {
        const totalQty = variantsByProduct[productId].reduce((sum, v) => sum + v.quantity, 0);
        this.cartState[productId] = totalQty;
      });
      
      this.container.querySelectorAll('.hits-add-form').forEach(form => {
        const addButton = form.querySelector('.hits-add-btn-icon');
        const productId = addButton?.dataset.productId;
        if (!productId) return;
        
        const qtyControls = form.querySelector('.hits-quantity-controls');
        const qtyDisplay = form.querySelector('.hits-qty-display');
        const currentActiveVariant = form.dataset.activeVariantId;

        if (variantsByProduct[productId] && variantsByProduct[productId].length > 0) {
          const match = currentActiveVariant
            ? variantsByProduct[productId].find(v => v.variantId === currentActiveVariant)
            : null;
          form.dataset.activeVariantId = (match ? match.variantId : variantsByProduct[productId][0].variantId);
          const input = form.querySelector('input[name="id"]');
          if (input) input.value = form.dataset.activeVariantId;
        } else {
          delete form.dataset.activeVariantId;
        }
        
        const priceDisplay = form.querySelector('.hits-add-wrapper__price');
        
        if (this.cartState[productId] > 0) {
          // Товар в корзине - скрываем цену и кнопку +, показываем контролы
          if (addButton) addButton.style.display = 'none';
          if (priceDisplay) priceDisplay.style.display = 'none';
          if (qtyControls) qtyControls.style.display = 'flex';
          if (qtyDisplay) qtyDisplay.textContent = this.cartState[productId];
        } else {
          // Товара нет - показываем цену и кнопку +, скрываем контролы
          if (addButton) addButton.style.display = 'flex';
          if (priceDisplay) priceDisplay.style.display = '';
          if (qtyControls) qtyControls.style.display = 'none';
        }
      });
    } catch (error) {
      // Silent error - will sync on next cart update
    }
  }

  setupEventListeners() {
    this.container.querySelectorAll('.hits-add-form').forEach(form => {
      const addButton = form.querySelector('.hits-add-btn-icon');
      const qtyControls = form.querySelector('.hits-quantity-controls');
      const qtyDisplay = form.querySelector('.hits-qty-display');
      const minusBtn = form.querySelector('[data-action="decrease"]');
      const plusBtn = form.querySelector('[data-action="increase"]');
      const productId = addButton?.dataset.productId;

      if (!productId) return;

      this.cartState[productId] = this.cartState[productId] || 0;

      const getVariantId = () => {
        const activeVariantId = form.dataset.activeVariantId;
        if (activeVariantId) return activeVariantId;
        const input = form.querySelector('input[name="id"]');
        return input ? input.value : addButton?.dataset.variantId;
      };

      const isQuickAddOpener = addButton?.dataset.openQuickAdd === 'true';

      addButton?.addEventListener('click', async (e) => {
        e.preventDefault();

        if (isQuickAddOpener) return;

        const wrapper = addButton?.closest('.hits-add-wrapper');
        const spinner = addButton?.querySelector('.loading__spinner');
        const btnSvg = addButton?.querySelector('svg');
        
        // Показываем спиннер и скрываем иконку
        if (spinner) spinner.classList.remove('hidden');
        if (btnSvg) btnSvg.style.visibility = 'hidden';
        if (addButton) addButton.style.pointerEvents = 'none';

        const cartDrawer = document.querySelector('cart-drawer');
        const prevQty = Number(this.cartState[productId] || 0);
        const shouldOpenDrawer = prevQty === 0;

        try {
          // Собираем items[] для запроса (товар + упаковка если нужно)
          const variantId = getVariantId();
          const productType = addButton.dataset.productType || '';
          const items = [{ id: parseInt(variantId), quantity: 1 }];

          // Проактивное добавление упаковки через PackagingUtils
          if (window.PackagingUtils) {
            const config = window.PackagingUtils.getConfig();
            if (config) {
              let cart = null;
              try { cart = await window.PackagingUtils.fetchFullCart(); } catch (_) {}
              const packagingIds = window.PackagingUtils.getPackagingItemsForAdd(
                cart, config.frozenId, config.cooledId, productType
              );
              packagingIds.forEach(id => items.push({ id: parseInt(id), quantity: 1 }));
            }
          }

          const sections = cartDrawer
            ? cartDrawer.getSectionsToRender().map(section => section.id)
            : [];

          if (cartDrawer) {
            cartDrawer.setActiveElement(document.activeElement);
          }

          const requestBody = { items, sections, sections_url: window.location.pathname };
          
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'X-Requested-With': 'XMLHttpRequest',
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestBody),
          });
          const responseData = await response.json();

          if (responseData.status) {
            alert(responseData.description || 'Не удалось добавить товар в корзину');
            return;
          }
          
          this.cartState[productId] = (this.cartState[productId] || 0) + 1;
          
          // Скрываем спиннер и восстанавливаем кнопку
          if (spinner) spinner.classList.add('hidden');
          if (btnSvg) btnSvg.style.visibility = '';
          if (addButton) addButton.style.pointerEvents = '';
          
          this.updateUI(form, productId);
          
          // Сбрасываем кеш упаковки после изменения корзины
          if (window.PackagingUtils) window.PackagingUtils.invalidateCache();

          // Получаем актуальные данные корзины с секциями для обновления UI
          const cartData = await this.fetchCartData(false);
          
          // Если ответ от add.js содержит секции, используем их, иначе запрашиваем
          let sectionsData = responseData;
          if (!responseData.sections || Object.keys(responseData.sections).length === 0) {
            // Запрашиваем полные данные с секциями
            const sectionsToFetch = cartDrawer 
              ? cartDrawer.getSectionsToRender().map(s => s.section || s.id)
              : [];
            if (sectionsToFetch.length > 0) {
              const sectionsResponse = await fetch(`${window.location.pathname}?sections=${sectionsToFetch.join(',')}`);
              const sectionsJson = await sectionsResponse.json();
              sectionsData = { ...cartData, sections: sectionsJson };
            }
          }
          
          const drawerIsActive = !!(cartDrawer && cartDrawer.classList.contains('active'));
          if (cartDrawer && (shouldOpenDrawer || drawerIsActive)) {
            cartDrawer.renderContents(sectionsData);
            this.updateCartHeader(cartData);
          } else {
            this.updateCartSectionsWithoutOpen(sectionsData);
            this.updateCartHeader(cartData);
          }
        } catch (error) {
          // Silent error - cart will sync on page refresh
          if (spinner) spinner.classList.add('hidden');
          if (btnSvg) btnSvg.style.visibility = '';
          if (addButton) addButton.style.pointerEvents = '';
        } finally {
          // Дополнительная гарантия сброса состояния (fallback)
          if (spinner) spinner.classList.add('hidden');
          if (btnSvg) btnSvg.style.visibility = '';
          if (addButton) addButton.style.pointerEvents = '';
          if (cartDrawer && cartDrawer.classList.contains('is-empty')) {
            cartDrawer.classList.remove('is-empty');
          }
        }
      });

      plusBtn?.addEventListener('click', async (e) => {
        e.preventDefault();
        this.cartState[productId]++;
        await this.updateCart(form, productId, getVariantId, false);
      });

      minusBtn?.addEventListener('click', async (e) => {
        e.preventDefault();
        if (this.cartState[productId] > 0) {
          this.cartState[productId]--;
          await this.updateCart(form, productId, getVariantId, false);
        }
      });
    });
  }

  async updateCart(form, productId, getVariantId, openDrawer = true) {
    const cartDrawer = document.querySelector('cart-drawer');
    
    try {
      const formData = new FormData();
      formData.append('id', getVariantId());
      formData.append('quantity', this.cartState[productId]);
      
      if (cartDrawer) {
        formData.append(
          'sections',
          cartDrawer.getSectionsToRender().map(section => section.id).join(',')
        );
        formData.append('sections_url', window.location.pathname);
        if (openDrawer) {
          cartDrawer.setActiveElement(document.activeElement);
        }
      }

      const config = {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      };

      const response = await fetch('/cart/change.js', config);
      const responseData = await response.json();

      if (responseData.status) {
        return;
      }
      
      this.updateUI(form, productId);
      
      // /cart/change.js возвращает полную корзину, используем её
      this.cartDataCache = responseData;
      this.cartDataCacheTime = Date.now();
      if (window.PackagingUtils) window.PackagingUtils.invalidateCache();
      
      // Если ответ от change.js не содержит секции, запрашиваем их
      let sectionsData = responseData;
      if (!responseData.sections || Object.keys(responseData.sections).length === 0) {
        const sectionsToFetch = cartDrawer 
          ? cartDrawer.getSectionsToRender().map(s => s.section || s.id)
          : [];
        if (sectionsToFetch.length > 0) {
          const sectionsResponse = await fetch(`${window.location.pathname}?sections=${sectionsToFetch.join(',')}`);
          const sectionsJson = await sectionsResponse.json();
          sectionsData = { ...responseData, sections: sectionsJson };
        }
      }
      
      const drawerIsActive = !!(cartDrawer && cartDrawer.classList.contains('active'));
      if (cartDrawer && (openDrawer || drawerIsActive)) {
        cartDrawer.renderContents(sectionsData);
        this.updateCartHeader(responseData);
      } else {
        this.updateCartSectionsWithoutOpen(sectionsData);
        this.updateCartHeader(responseData);
      }
    } catch (error) {
      // Silent error - cart will sync on page refresh
    } finally {
      if (cartDrawer && cartDrawer.classList.contains('is-empty')) {
        cartDrawer.classList.remove('is-empty');
      }
    }
  }

  updateUI(form, productId) {
    const addButton = form.querySelector('.hits-add-btn-icon');
    const qtyControls = form.querySelector('.hits-quantity-controls');
    const qtyDisplay = form.querySelector('.hits-qty-display');
    const priceDisplay = form.querySelector('.hits-add-wrapper__price');

    if (this.cartState[productId] > 0) {
      // Товар в корзине - скрываем цену и кнопку +, показываем контролы количества
      if (addButton) addButton.style.display = 'none';
      if (priceDisplay) priceDisplay.style.display = 'none';
      if (qtyControls) qtyControls.style.display = 'flex';
      if (qtyDisplay) qtyDisplay.textContent = this.cartState[productId];
    } else {
      // Товара нет в корзине - показываем цену и кнопку +, скрываем контролы
      if (addButton) addButton.style.display = 'flex';
      if (priceDisplay) priceDisplay.style.display = '';
      if (qtyControls) qtyControls.style.display = 'none';
    }
  }
}

// Auto-initialize for entire document
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new ProductCardActions();
  });
} else {
  new ProductCardActions();
}

// Export for manual initialization if needed
window.ProductCardActions = ProductCardActions;
